import React, { useState, useEffect, useCallback } from 'react';
import {
    Typography, Paper, Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Card, CardContent, Grid, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, TextField, IconButton
} from '@mui/material';
import api from '../api/axiosInstance';
import Papa from 'papaparse';
import { useSnackbar } from '../context/SnackbarContext';
import {
    FileDownload as FileDownloadIcon,
    FileUpload as FileUploadIcon,
    Save as SaveIcon,
    Edit as EditIcon,
    Close as CloseIcon,
} from '@mui/icons-material';

export default function StudentData() {
    const showSnackbar = useSnackbar();
    const [schema, setSchema] = useState([]);
    const [dbStudents, setDbStudents] = useState([]); // Students from DB
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [openConfirm, setOpenConfirm] = useState(false);

    // Inline Editing States
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});

    // const token = localStorage.getItem('token');

    // --- 1. Fetch Schema and Current Students ---
    // const fetchData = useCallback(async () => {
    //     setLoading(true);
    //     try {
    //         // Fetch Settings for Schema
    //         const settingsRes = await fetch(`${process.env.REACT_APP_API_URL}/api/settings`, {
    //             headers: { 'Authorization': `Bearer ${token}` }
    //         });
    //         const settingsData = await settingsRes.json();

    //         let currentSchema = [];
    //         if (settingsData.student_import_schema) {
    //             currentSchema = typeof settingsData.student_import_schema === 'string'
    //                 ? JSON.parse(settingsData.student_import_schema)
    //                 : settingsData.student_import_schema;
    //             setSchema(currentSchema);
    //         }

    //         // Fetch Current Students
    //         const studentsRes = await fetch(`${process.env.REACT_APP_API_URL}/api/students`, {
    //             headers: { 'Authorization': `Bearer ${token}` }
    //         });
    //         const studentsData = await studentsRes.json();

    //         // Map the DB JSONB payload to a flat structure for the table
    //         const flattenedStudents = studentsData.map(s => ({
    //             db_id: s.id, // The UUID from Postgres
    //             ...s.payload
    //         }));

    //         setDbStudents(flattenedStudents);
    //     } catch (err) {
    //         showSnackbar('Error loading data', 'error');
    //     } finally {
    //         setLoading(false);
    //     }
    // }, [token, showSnackbar]);

    // --- 1. Fetch Schema and Current Students ---
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Parallel fetch for settings and students
            const [settingsRes, studentsRes] = await Promise.all([
                api.get('/settings'),
                api.get('/students')
            ]);

            // Handle Schema
            const schemaData = settingsRes.data.student_import_schema;
            const currentSchema = typeof schemaData === 'string'
                ? JSON.parse(schemaData)
                : schemaData || [];
            setSchema(currentSchema);

            // Handle Students - Flattening the JSONB payload for the table
            const flattenedStudents = studentsRes.data.map(s => ({
                db_id: s.id,
                ...s.payload
            }));
            setDbStudents(flattenedStudents);

        } catch (err) {
            console.error(err);
            showSnackbar('Error loading student data', 'error');
        } finally {
            setLoading(false);
        }
    }, [showSnackbar]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- 2. Inline Edit Logic ---
    const startEdit = (student) => {
        setEditingId(student.db_id);
        setEditForm({ ...student });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm({});
    };

    const handleEditChange = (id, value) => {
        setEditForm(prev => ({ ...prev, [id]: value }));
    };

    // const saveEdit = async (db_id) => {
    //     try {
    //         const { db_id: _, ...payloadOnly } = editForm; // Remove DB id from payload
    //         const res = await fetch(`${process.env.REACT_APP_API_URL}/api/students/${db_id}`, {
    //             method: 'PUT',
    //             headers: {
    //                 'Content-Type': 'application/json',
    //                 'Authorization': `Bearer ${token}`
    //             },
    //             body: JSON.stringify({ payload: payloadOnly })
    //         });

    //         if (res.ok) {
    //             showSnackbar('Student updated', 'success');
    //             setEditingId(null);
    //             fetchData(); // Refresh list
    //         }
    //     } catch (err) {
    //         showSnackbar('Failed to update student', 'error');
    //     }
    // };

    const saveEdit = async (db_id) => {
        try {
            // eslint-disable-next-line no-unused-vars
            const { db_id: _, ...payloadOnly } = editForm;
            await api.put(`/students/${db_id}`, { payload: payloadOnly });

            showSnackbar('Student updated', 'success');
            setEditingId(null);
            fetchData();
        } catch (err) {
            showSnackbar('Failed to update student', 'error');
        }
    };

    // --- 3. CSV Actions (Download/Upload) ---
    const downloadTemplate = () => {
        if (schema.length === 0) {
            showSnackbar('No schema defined in settings', 'warning');
            return;
        }
        const headers = schema.map(col => col.label);
        const csv = Papa.unparse({ fields: headers, data: [] }, { quotes: true });
        const blob = new Blob(["\ufeff" + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `StudentData_template_${new Date().toISOString().split('T')[0]}.csv`);
        link.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const mappedRows = results.data.map(row => {
                    const studentObj = {};
                    schema.forEach(config => {
                        studentObj[config.id] = row[config.label];
                    });
                    return studentObj;
                });
                setPreviewData(mappedRows);
            }
        });
        // Reset input so the same file can be uploaded twice if needed
        event.target.value = '';
    };

    const handleImport = async () => {
        setUploading(true);
        try {
            await api.post('/students/import', { students: previewData });
            showSnackbar('Data imported successfully!', 'success');
            setPreviewData(null);
            fetchData();
        } catch (err) {
            showSnackbar('Import failed', 'error');
        } finally {
            setUploading(false);
        }
    };

    if (loading && dbStudents.length === 0)
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: 15 // Pushed down a bit more for visual balance
                }}
            >
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    {/* 1. The Background Track (Static) */}
                    <CircularProgress
                        variant="determinate"
                        sx={{ color: 'grey.200' }}
                        size={80} // Increased size
                        thickness={6}
                        value={100}
                    />
                    {/* 2. The Active Spinner (Moving) */}
                    <CircularProgress
                        variant="indeterminate"
                        disableShrink // Makes it look like a steady "long" snake
                        sx={{
                            color: 'primary.main', // Or 'black' as you had it
                            animationDuration: '600ms', // Faster spin feels more energetic
                            position: 'absolute',
                            left: 0,
                            [`& .MuiCircularProgress-circle`]: {
                                strokeLinecap: 'round', // Rounded edges look much more modern
                            },
                        }}
                        size={80}
                        thickness={6}
                    />
                </Box>

                {/* 3. Status Text */}
                <Typography
                    variant="h6"
                    sx={{ mt: 3, fontWeight: 500, color: 'text.secondary', letterSpacing: 1 }}
                >
                    LOADING STUDENT DATA...
                </Typography>
            </Box>
        );

    return (
        <Box sx={{
            height: '100%',
            display: 'flex',
            p: 3,
            flexDirection: 'column',
            // overflow: 'hidden' // Prevents the whole page from scrolling
        }}>

            {/* --- THIS IS THE "NON-SHRINKING" WRAPPER --- */}
            <Box sx={{ flexShrink: 0, mb: 2 }}>

                <Typography variant="h4" gutterBottom>
                    Student Data Management
                </Typography>

                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    Student data that will be used in ceremonies and awards, etc.
                </Typography>

                <Grid container spacing={3} sx={{ mb: 3 }}>
                    {/* Action Cards */}
                    <Grid item size={5} md={6}>
                        <Card sx={{ height: '100%', boxShadow: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>1. Get CSV Template</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Download a template with your current column configuration.
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<FileDownloadIcon />}
                                    onClick={downloadTemplate}
                                    disabled={schema.length === 0}
                                >
                                    Download Template
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item size={5} md={6}>
                        <Card sx={{ height: '100%', boxShadow: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>2. Upload Student Data</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Upload your populated CSV file to update the database.
                                </Typography>
                                <Button
                                    variant="contained"
                                    component="label"
                                    startIcon={<FileUploadIcon />}
                                >
                                    Upload CSV File
                                    <input type="file" hidden accept=".csv" onChange={handleFileChange} />
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

            </Box>


            {/* Main Area (Grows to fill remaining space) */}
            <Box sx={{
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                mb: 4
            }}>

                {/* Import Preview */}
                {previewData && (
                    <Paper sx={{
                        p: 3,
                        border: '2px solid #4caf50',
                        flexShrink: 0,
                        mx: 0 // Ensure no horizontal margin 
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6" color="success.main">Preview: New Import ({previewData.length} records)</Typography>
                            <Button variant="contained" color="success" onClick={() => setOpenConfirm(true)}>Confirm Replace</Button>
                        </Box>
                        <TableContainer sx={{ maxHeight: 250 }}>
                            <Table size="small">
                                <TableHead><TableRow>{schema.map(c => <TableCell key={c.id}>{c.label}</TableCell>)}</TableRow></TableHead>
                                <TableBody>
                                    {previewData.slice(0, 10).map((row, i) => (
                                        <TableRow key={i}>{schema.map(c => <TableCell key={c.id}>{row[c.id]}</TableCell>)}</TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {/* Current Database Table */}

                <Paper sx={{
                    p: 0,
                    boxShadow: 3,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1, // Makes the Paper fill the Box
                    minHeight: 200,
                    mx: 0 // Ensure no horizontal margin
                }}>
                    <Box sx={{ p: 1.5, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            Current Students ({dbStudents.length})
                        </Typography>
                    </Box>
                    <TableContainer sx={{
                        flexGrow: 1,
                        overflow: 'auto' // Enables the scrollbar
                    }}>
                        <Table
                            stickyHeader
                            size="small"
                            sx={{
                                minWidth: '100%',
                                tableLayout: 'auto', // Allows columns to fit content but fill width
                                // '& .MuiTableCell-root': {
                                //     paddingY: '4px', // Significantly reduces row height
                                //     paddingX: '8px',
                                //     fontSize: '0.8125rem' // Slightly smaller font for density
                                // }
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', width: 80 }}>Actions</TableCell>
                                    {schema.map(col => (
                                        <TableCell key={col.id} sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                                            {col.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dbStudents.map((student) => (
                                    <TableRow key={student.db_id} hover>
                                        <TableCell>
                                            {editingId === student.db_id ? (
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <IconButton size="small" onClick={() => saveEdit(student.db_id)} color="success">
                                                        <SaveIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={cancelEdit} color="error">
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            ) : (
                                                <IconButton size="small" onClick={() => startEdit(student)} color="primary">
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                        {schema.map(col => (
                                            <TableCell key={col.id}>
                                                {editingId === student.db_id ? (
                                                    <TextField
                                                        variant="standard"
                                                        size="small"
                                                        value={editForm[col.id] || ''}
                                                        onChange={(e) => handleEditChange(col.id, e.target.value)}
                                                        fullWidth
                                                        inputProps={{ style: { fontSize: '0.8125rem', padding: '2px 0' } }}
                                                    />
                                                ) : (
                                                    student[col.id]
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>

            {/* Confirmation Dialog */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle>Replace All Student Data?</DialogTitle>
                <DialogContent><DialogContentText>This wipes the current database and replaces it with the CSV data.</DialogContentText></DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
                    <Button onClick={() => { setOpenConfirm(false); handleImport(); }} disabled={uploading} color="error">Replace</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}